/**
 * Phase 13 Beat 2 (Feb 2026) — Candidate Matching Engine · UI demo panel.
 *
 * Minimal UI to demonstrate the Beat 2 engine end-to-end (per user instruction:
 * "First build and verify the matching engine and its results — no full
 * Recommended Jobs feed until Beat 3+"). This lets an admin/exec pick any
 * candidate on a Job Detail page and see the score + factor breakdown.
 *
 * Wired to:
 *   GET /api/candidates?q={term}
 *   GET /api/matches/pair?candidate_id={cid}&job_id={jid}
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  MagnifyingGlass, Sparkle, Warning, CheckCircle, XCircle, Question,
} from "@phosphor-icons/react";

const STATUS_META = {
  match:    { icon: CheckCircle, tone: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Match" },
  partial:  { icon: CheckCircle, tone: "text-lime-700    bg-lime-50    border-lime-200",    label: "Partial" },
  gap:      { icon: Warning,     tone: "text-amber-700   bg-amber-50   border-amber-200",   label: "Gap" },
  mismatch: { icon: XCircle,     tone: "text-red-700     bg-red-50     border-red-200",     label: "Mismatch" },
  unknown:  { icon: Question,    tone: "text-slate-600   bg-slate-100  border-slate-200",   label: "Unknown" },
  na:       { icon: Question,    tone: "text-slate-500   bg-slate-100  border-slate-200",   label: "N/A" },
};

const BAND_TONE = {
  "Highly Relevant": "bg-emerald-600 text-white",
  "Relevant":        "bg-sky-600 text-white",
  "Consider":        "bg-amber-500 text-white",
  "Low Match":       "bg-slate-500 text-white",
};

const FACTOR_ORDER = [
  "role", "skills", "responsibilities", "experience",
  "education", "location", "salary", "visa",
];

const FACTOR_LABEL = {
  role: "Role", skills: "Skills", responsibilities: "Responsibilities",
  experience: "Experience", education: "Education", location: "Location",
  salary: "Salary", visa: "Work Authorization",
};

export default function MatchPreviewPanel({ jobId }) {
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [computing, setComputing] = useState(false);
  const [match, setMatch] = useState(null);

  // Debounced candidate search
  useEffect(() => {
    if (!query || query.length < 2) { setCandidates([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/candidates?q=${encodeURIComponent(query)}&page_size=8`);
        setCandidates(data.candidates || data.rows || data || []);
      } catch {
        setCandidates([]);
      } finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const runMatch = async (cid) => {
    setComputing(true); setMatch(null);
    try {
      const { data } = await api.get(`/matches/pair?candidate_id=${cid}&job_id=${jobId}`);
      setMatch(data);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Match compute failed");
    } finally { setComputing(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="match-preview-panel">
      <div className="flex items-center gap-2 mb-3">
        <Sparkle size={16} weight="fill" className="text-indigo-500" />
        <h3 className="text-sm font-semibold text-slate-900">Match Preview</h3>
        <span className="text-[11px] text-slate-500">
          Test how this job scores against a candidate profile
        </span>
      </div>

      {/* Candidate typeahead */}
      <div className="relative">
        <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 focus-within:ring-2 focus-within:ring-indigo-500/30 focus-within:border-indigo-500">
          <MagnifyingGlass size={14} className="text-slate-400" />
          <input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
            placeholder="Search candidates by name, email, skills, role…"
            data-testid="match-preview-search"
            className="flex-1 bg-transparent outline-none text-sm"
          />
          {searching && <div className="text-[11px] text-slate-500">Searching…</div>}
        </div>
        {candidates.length > 0 && !selected && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto"
               data-testid="match-preview-results">
            {candidates.map((c) => (
              <button
                key={c.id}
                onClick={() => { setSelected(c); setQuery(c.full_name || c.email || c.id); setCandidates([]); runMatch(c.id); }}
                data-testid={`match-preview-candidate-${c.id}`}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b border-slate-100 last:border-b-0"
              >
                <div className="font-medium text-slate-800">{c.full_name || "—"}</div>
                <div className="text-xs text-slate-500">
                  {c.preferred_role || "—"} · {c.email || "—"}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {computing && (
        <div className="mt-4 text-sm text-slate-500" data-testid="match-preview-loading">
          Computing match…
        </div>
      )}

      {match && !computing && (
        <div className="mt-5 space-y-4" data-testid="match-preview-result">
          {/* Overall score header */}
          <div className="flex items-center gap-3">
            <div className="relative w-20 h-20 shrink-0" data-testid="match-preview-score">
              <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none" stroke="#e2e8f0" strokeWidth="3" />
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none" stroke="#4f46e5" strokeWidth="3"
                      strokeDasharray={`${match.overall_score}, 100`} />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center text-xl font-bold text-slate-900">
                {match.overall_score}%
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${BAND_TONE[match.band] || "bg-slate-500 text-white"}`}
                      data-testid="match-preview-band">
                  {match.band}
                </span>
                {match.cache_hit && (
                  <span className="text-[10px] text-slate-500 uppercase tracking-wide">cached</span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Active weight share: {match.active_weight_share}% · Computed {new Date(match.computed_at).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Review Required banner */}
          {match.review_required && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-900"
                 data-testid="match-preview-review-required">
              <div className="font-semibold inline-flex items-center gap-1.5">
                <Warning size={14} weight="fill" /> Review Required
              </div>
              <ul className="mt-1 list-disc list-inside space-y-0.5 text-xs">
                {match.review_reasons.map((r) => (
                  <li key={`rr-${r}`}>{r}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Per-factor breakdown */}
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="match-preview-factors">
            {FACTOR_ORDER.map((k) => {
              const f = match.factors[k];
              if (!f) return null;
              const meta = STATUS_META[f.status] || STATUS_META.unknown;
              const Icon = meta.icon;
              return (
                <div key={k} className="p-3" data-testid={`match-preview-factor-${k}`}>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${meta.tone} inline-flex items-center gap-1`}>
                      <Icon size={11} weight="fill" /> {meta.label}
                    </span>
                    <span className="text-sm font-medium text-slate-800 flex-1">{FACTOR_LABEL[k]}</span>
                    <span className="text-xs text-slate-500 tabular-nums">
                      {f.score !== null && f.score !== undefined ? `${f.score}` : "—"}
                      <span className="text-slate-400"> · weight {f.weight}%</span>
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1.5">{f.explanation}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
